import React from 'react';
import { Users, Sparkles, ArrowRight, Compass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const StatBadge = ({ number, label, accent = 'teal' }) => {
  const colors = {
    teal:   { text: 'text-teal-300',   ring: 'ring-teal-400/40' },
    amber:  { text: 'text-amber-300',  ring: 'ring-amber-400/40' },
  };
  const c = colors[accent];
  return (
    <div className={`inline-flex items-baseline gap-2 px-4 py-2 rounded-xl bg-white/5 ring-1 ${c.ring} backdrop-blur-sm`}>
      <span className={`${c.text} font-black text-2xl`} style={{ fontFamily: "'Playfair Display', serif" }}>{number}</span>
      <span className="text-slate-300 text-xs uppercase tracking-widest font-semibold">{label}</span>
    </div>
  );
};

const StrongerTogetherSection = () => {
  const navigate = useNavigate();
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-[#0A2037] via-[#0d2a45] to-[#079897]"
      data-testid="homepage-stronger-together-section"
    >
      {/* Decorative glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[520px] h-[520px] bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-amber-400/8 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">

        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/40 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-amber-200 text-[11px] font-bold tracking-[0.2em] uppercase">DCELC Initiative</span>
          </div>

          <h2
            className="text-white font-bold leading-[0.95] mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(48px, 8vw, 84px)' }}
          >
            Stronger <span className="text-teal-300 italic">Together</span>
          </h2>

          <p className="text-teal-200 text-sm sm:text-base font-medium tracking-wide mb-6 max-w-2xl">
            A DCELC Initiative for Collective Leadership in Early Childhood Education
          </p>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-teal-300/30 backdrop-blur-sm">
            <ArrowRight className="w-3.5 h-3.5 text-teal-300" />
            <span className="text-teal-200 text-[11px] font-bold tracking-[0.22em] uppercase">From a Convening to a Movement</span>
          </div>
        </div>

        {/* Story: opening */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="text-slate-100 text-lg leading-relaxed">
              In <strong className="text-teal-300">May 2026</strong>, more than 70 early childhood leaders, educators, and advocates gathered for <em className="text-amber-200 not-italic font-semibold">Stronger Together: Protecting Progress Through Collective Leadership</em> &mdash; a convening born from a simple but urgent recognition: <span className="text-white font-semibold">Washington, DC&apos;s early childhood education field is strongest when it moves as one.</span>
            </p>

            <p className="mt-5 text-slate-300 text-base leading-relaxed">
              That single gathering is now becoming something bigger. <strong className="text-teal-200">Stronger Together</strong> is DCELC&apos;s ongoing initiative to protect progress in DC&apos;s early childhood field through collective leadership &mdash; and it now serves as the <span className="text-white font-semibold">connective infrastructure of our ECE as a Public Good campaign</span>.
            </p>

            <Button
              onClick={() => navigate('/programs/stronger-together')}
              className="mt-6 bg-amber-400 hover:bg-amber-300 text-[#0A2037] font-bold px-6 py-3 shadow-lg shadow-amber-400/25"
              data-testid="stronger-together-full-story"
            >
              Explore Stronger Together
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Right rail: stats */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            <StatBadge number="70+" label="Leaders Convened, May 2026" accent="teal" />
            <StatBadge number="1" label="Field. One Voice." accent="amber" />
            <div className="rounded-xl bg-white/5 border border-teal-300/30 backdrop-blur-sm p-5">
              <div className="flex items-center gap-2 text-teal-300 text-[10px] font-bold tracking-[0.2em] uppercase mb-2">
                <Compass className="w-3.5 h-3.5" />
                Core Belief
              </div>
              <p className="text-white text-sm font-medium leading-snug">
                Leadership doesn&apos;t happen by accident. It has to be <span className="text-teal-300">cultivated</span>, <span className="text-teal-300">trained</span>, and <span className="text-teal-300">resourced</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-14 flex items-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-teal-300/40 to-transparent flex-1" />
          <Users className="w-4 h-4 text-teal-300" />
          <div className="h-px bg-gradient-to-r from-transparent via-teal-300/40 to-transparent flex-1" />
        </div>

      </div>
    </section>
  );
};

export default StrongerTogetherSection;
